const ACCESS_TOKEN_KEY = 'wh_access_token';
const REFRESH_TOKEN_KEY = 'wh_refresh_token';

const apiBaseUrl = process.env.REACT_APP_WALTERHOUSE_API_URL || 'https://api.walterhouse.co.za/api';

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function getAccessToken() {
  if (!canUseStorage()) return null;
  return window.localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  if (!canUseStorage()) return null;
  return window.localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setAccessToken(accessToken) {
  if (!canUseStorage()) return;
  window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
}

export function clearTokens() {
  if (!canUseStorage()) return;
  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_KEY);
}

async function apiRequest(path, options = {}) {
  const { token, headers, ...rest } = options;

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers || {})
    }
  });

  const text = await response.text();
  const json = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = new Error(json?.message || json?.error || response.statusText || 'Request failed');
    error.status = response.status;
    throw error;
  }

  return json?.data ?? json;
}

function mapUser(user) {
  if (!user) return null;
  return {
    id: user.id,
    uid: user.id,
    email: user.email,
    displayName: user.display_name || user.displayName || null,
    username: user.username || null,
    photoUrl: user.photo_url || user.photoUrl || null
  };
}

function mapStats(stats, fallbackGameId) {
  if (!stats) {
    return {
      gameId: fallbackGameId,
      highScore: 0,
      playCount: 0,
      lastPlayed: null
    };
  }

  return {
    gameId: stats.gameId || stats.game_id || fallbackGameId,
    highScore: stats.highScore ?? stats.high_score ?? 0,
    playCount: stats.playCount ?? stats.play_count ?? 0,
    lastPlayed: stats.lastPlayed ?? stats.last_played ?? null
  };
}

export async function refreshAccessToken(refreshToken) {
  return apiRequest('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken })
  });
}

export async function getCurrentUser(accessToken) {
  const user = await apiRequest('/auth/me', {
    method: 'GET',
    token: accessToken
  });

  return mapUser(user);
}

export async function fetchGameStats(gameIdOrSlug, accessToken) {
  const response = await apiRequest(`/games/${gameIdOrSlug}/stats`, {
    method: 'GET',
    token: accessToken
  });

  return {
    game: response.game,
    stats: mapStats(response.stats, response.game?.id || gameIdOrSlug)
  };
}

export async function fetchGames() {
  return apiRequest('/games', {
    method: 'GET'
  });
}

export async function updateGameStats(gameIdOrSlug, payload, accessToken) {
  const response = await apiRequest(`/games/${gameIdOrSlug}/stats`, {
    method: 'POST',
    token: accessToken,
    body: JSON.stringify({
      highScore: payload.highScore,
      playCount: payload.playCount,
      lastPlayed: payload.lastPlayed,
      sessionId: payload.sessionId || null
    })
  });

  return mapStats(response, response.gameId || response.game_id || gameIdOrSlug);
}
